/**
 * 金額ユーティリティ。**金額は常に整数(円)。** 小数・負値はDBに入れない。
 */
import type { Yen } from './types';

/** 1件あたりの上限(誤入力の歯止め)。これを超える取引は画面で確認を求める */
export const MAX_AMOUNT: Yen = 1_000_000_000;

/** DBに保存できる金額か(整数・0より大きい・上限以内) */
export function isValidAmount(v: unknown): v is Yen {
  return typeof v === 'number' && Number.isInteger(v) && v > 0 && v <= MAX_AMOUNT;
}

/** 3桁区切り。例) 1234567 → '1,234,567' */
export function formatYen(v: Yen): string {
  return v.toLocaleString('ja-JP');
}

/** 通貨記号つき。例) 1234567 → '¥1,234,567' */
export function formatYenSigned(v: Yen): string {
  return `¥${formatYen(v)}`;
}

/** 差異表示。例) 12000 → '+12,000' / -3000 → '-3,000' */
export function formatDiff(v: number): string {
  return `${v >= 0 ? '+' : ''}${v.toLocaleString('ja-JP')}`;
}

/** 入力文字列(全角数字・カンマ・空白を含みうる)を整数に変換する。不正なら null */
export function parseAmount(input: string): Yen | null {
  const normalized = input
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/[,，\s円]/g, '')
    .trim();
  if (normalized === '' || !/^\d+$/.test(normalized)) return null;
  const n = Number(normalized);
  return isValidAmount(n) ? n : null;
}

/** 達成率(%)。予算0のときは null(画面では「—」を表示する) */
export function achievementRate(budget: Yen, actual: Yen): number | null {
  if (budget === 0) return null;
  return Math.round((actual / budget) * 100);
}

/**
 * 入力中の金額文字列を3桁カンマ付きに整形する(■12・2026-08-28 共通化)。
 * 全角数字は半角化し、数字以外は取り除く
 */
export function formatAmountInput(raw: string): string {
  const digits = raw
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    // 数字以外(カンマ・空白など)を取り除く。以前は /D/ と書かれていてカンマが残り、
    // 5桁目を入力すると "NaN" になって直せなくなっていた(2026-09-07 指摘)
    .replace(/\D/g, '');
  if (digits === '') return '';
  return Number(digits).toLocaleString('ja-JP');
}
